'use client';

import React, { useEffect, useState } from "react";
import {
    Box, Button, Typography, List, ListItem, Switch,
    Accordion, AccordionSummary, AccordionDetails,
    SwipeableDrawer, FormControl, InputLabel, OutlinedInput,
} from "@mui/material";
import Link from 'next/link';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import CircleIcon from '@mui/icons-material/Circle';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import { useRouter, usePathname } from "next/navigation";
import useScreenSize from "@/utils/useScreenSize";
import { DynamicFilter, DynamicFilterItem } from "@/store/productSlice";

interface DynamicFilterPanelProps {
    dynamicFilters: DynamicFilter[];
    dynFiltersParam?: string;
    sortBy?: string;
    cssStyles: { [key: string]: string };
}

/**
 * Client island for dynamic filters (popover/drawer), sort-by, and sale toggle.
 * Used by both PLP and Search pages.
 */
const DynamicFilterPanel: React.FC<DynamicFilterPanelProps> = ({
    dynamicFilters,
    dynFiltersParam,
    sortBy,
    cssStyles,
}) => {
    const pathname = usePathname();
    const router = useRouter();
    const screenSize = useScreenSize();
    const [isClient, setIsClient] = useState(false);
    useEffect(() => { setIsClient(true); }, []);

    const [activeDynFilterName, setActiveDynFilterName] = useState<string>(
        dynamicFilters && dynamicFilters.length > 0 ? dynamicFilters[0].name : ""
    );
    const [openFilterMobile, setOpenFilterMobile] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [sortBySelected, setSortBySelected] = useState(sortBy);

    // ---------- Dynamic Filter Link Builder ----------
    const getDynMenuLink = (filterName: string | null, facetName: string | null, remove: boolean = false) => {
        let dynFilterStr = "";
        const dynFiltersParamMap = new Map<string, string[]>();
        const dynFiltersParamArr = dynFiltersParam?.split("~") || [];
        dynFiltersParamArr.forEach((dynFilter: string) => {
            const [fName, fValue] = dynFilter.split(":");
            dynFiltersParamMap.set(fName, fValue.split(","));
        });
        if (filterName && facetName) {
            let filterItems = dynFiltersParamMap.get(filterName);
            if (remove) {
                filterItems?.splice(filterItems.indexOf(facetName), 1);
            } else {
                if (filterItems && !filterItems.includes(facetName)) {
                    filterItems.push(facetName);
                } else {
                    filterItems = [facetName];
                }
            }
            dynFiltersParamMap.set(filterName, filterItems || []);
            dynFiltersParamMap.forEach((items: string[], name: string) => {
                if (items?.length > 0) {
                    dynFilterStr += dynFilterStr ? "~" : "";
                    dynFilterStr += name + ":" + items.join(",");
                }
            });
        }
        let updatedPathName = pathname;
        if (dynFilterStr) {
            if (dynFiltersParam && pathname.includes("/filters")) {
                updatedPathName = decodeURIComponent(pathname).replace(dynFiltersParam, dynFilterStr);
            } else {
                updatedPathName = pathname + '/filters/' + dynFilterStr;
            }
        } else {
            if (dynFiltersParam && pathname.includes("/filters")) {
                updatedPathName = decodeURIComponent(pathname).replace('/filters/' + dynFiltersParam, '');
            }
        }
        return updatedPathName;
    };

    const getDisplayName = (filterValue: string, filterName: string) => {
        for (let eachFilter of dynamicFilters) {
            if (eachFilter?.name === filterName) {
                const matchedItem = eachFilter?.filterItems?.find((item: any) => item.filterValue === filterValue);
                if (matchedItem) return matchedItem?.displayname;
            }
        }
        return '';
    };

    // ---------- Sort By ----------
    const handleSortByChange = (event: SelectChangeEvent) => {
        setSortBySelected(event.target.value as string);
    };

    useEffect(() => {
        if (sortBySelected) {
            if (pathname.includes("/sortBy")) {
                router.push(pathname.replace(/sortBy\/\w+/, `sortBy/${sortBySelected}`));
            } else {
                router.push(pathname + `/sortBy/${sortBySelected}`);
            }
        }
    }, [sortBySelected]);

    const buildSortByMenu = () => {
        const sortOptions: { [key: string]: string } = {
            recommended: "Recommended",
            priceAsc: "Price: Low to High",
            priceDesc: "Price: High to Low",
        };
        const ITEM_HEIGHT = 48;
        const ITEM_PADDING_TOP = 8;
        const MenuProps = {
            PaperProps: { style: { maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP, width: 100 } },
        };
        return (
            <Box component="div" className={cssStyles.filter_by_desktop_wrapper}>
                <Box component="div" className={cssStyles.filter_by_list}>
                    <FormControl sx={{ m: 1, width: 'auto' }}>
                        <InputLabel id="sort-by-label">
                            <Typography className={cssStyles.sub_heading} sx={{ paddingRight: '5px', background: '#fff' }}>SORT BY</Typography>
                        </InputLabel>
                        <Select
                            labelId="sort-by-label"
                            id="sort-by"
                            value={sortBySelected || "recommended"}
                            onChange={handleSortByChange}
                            input={<OutlinedInput label="SORT BY" />}
                            MenuProps={MenuProps}
                        >
                            {Object.entries(sortOptions).map(([name, displayName]) => (
                                <MenuItem key={name} value={name} sx={{ paddingLeft: '10px' }}>
                                    <Typography variant="h6">{displayName}</Typography>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>
        );
    };

    // ---------- Filter handlers ----------
    const handleClickFilterPopover = (event: React.MouseEvent<HTMLButtonElement>, filterObj: any) => {
        setAnchorEl(event.currentTarget);
        dynamicFilters?.forEach((df: DynamicFilter) => {
            df.isActive = df.name === filterObj.name;
            if (df.isActive) setActiveDynFilterName(df.name);
        });
    };

    const handleSalesToggleButtonChange = (dynamicFilter: DynamicFilter, event: React.ChangeEvent<HTMLInputElement>) => {
        dynamicFilter.hideFromSelection = true;
        const filterName = dynamicFilter.name;
        const filterValue = dynamicFilter?.filterItems && dynamicFilter?.filterItems?.length > 0
            ? dynamicFilter.filterItems[0].filterValue : 'true';
        dynamicFilter.selectedFilterValues = event.target.checked ? [filterValue] : [];
        const link = getDynMenuLink(filterName, filterValue, !event.target.checked);
        router.push(link);
    };

    const handleCloseFilterPopover = () => { setAnchorEl(null); };
    const openFilterDesktop = Boolean(anchorEl);
    const toggleFilterMobile = (newOpen: boolean) => () => { setOpenFilterMobile(newOpen); };

    const getSaleFilter = () => {
        return dynamicFilters.filter((df: DynamicFilter) => df.name === "sale").map((df: DynamicFilter) => (
            <Button
                key={"dynFilter-" + df.name}
                className={cssStyles.filter_list}
                sx={{ border: 'none', backgroundColor: 'transparent', padding: '0px', '&:hover': { backgroundColor: 'transparent' } }}
            >
                <Typography className={cssStyles.menu_text_bold} sx={{ color: 'secondary.dark' }}>{df.name}</Typography>
                <Switch
                    checked={df.selectedFilterValues && df.selectedFilterValues?.length > 0 || false}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleSalesToggleButtonChange(df, event)}
                />
            </Button>
        ));
    };

    if (!dynamicFilters || dynamicFilters.length === 0 || !activeDynFilterName) {
        return null;
    }

    return (
        <div className={cssStyles.bRoot}>
            {isClient && screenSize.width > 768 ? (
                <>
                    <Box component="div" className={cssStyles.filter_by_desktop_wrapper}>
                        <Box component="div" className={cssStyles.filter_by_label}>
                            <Typography className={cssStyles.sub_heading}>FILTER BY</Typography>
                        </Box>
                        <Box component="div" className={cssStyles.filter_by_list}>
                            {dynamicFilters.filter((df: DynamicFilter) => df.name !== "sale").map((df: DynamicFilter) => (
                                <Button
                                    key={"dynFilter-" + df.name}
                                    className={cssStyles.filter_list}
                                    sx={{
                                        borderWidth: '1px',
                                        borderStyle: 'solid',
                                        borderColor: 'secondary.dark',
                                        '&:hover': { borderColor: 'primary.main' },
                                        '&:hover *': { color: 'primary.main' },
                                    }}
                                    endIcon={<ArrowDropDownIcon />}
                                    onClick={(e) => handleClickFilterPopover(e, { name: df.name })}
                                >
                                    <Typography className={cssStyles.menu_text} sx={{ color: 'secondary.dark', '&:hover': { color: 'primary.main' } }}>
                                        {df.name}
                                    </Typography>
                                </Button>
                            ))}
                        </Box>
                        <Box component="div">{getSaleFilter()}</Box>
                        <Box component="div" sx={{ marginLeft: 'auto' }}>{buildSortByMenu()}</Box>
                        <Popover
                            id={'filter-popover'}
                            open={openFilterDesktop}
                            anchorEl={anchorEl}
                            onClose={handleCloseFilterPopover}
                            elevation={2}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            sx={{ '.MuiPaper-root': { borderRadius: '20px' } }}
                        >
                            <Box component='div' sx={{ padding: '10px', display: 'flex', flexDirection: 'column' }}>
                                {dynamicFilters.filter((df: DynamicFilter) => df.name !== "sale").map((df: DynamicFilter) => (
                                    <React.Fragment key={"popover-" + df.name}>
                                        {df.name === activeDynFilterName && (
                                            <List sx={{ columns: df?.filterItems ? Math.max(1, Math.round(df.filterItems.length / 5)) : 1 }}>
                                                {df?.filterItems?.map((item: DynamicFilterItem) => {
                                                    const isSelected = df?.selectedFilterValues?.includes(item.filterValue) || false;
                                                    return (
                                                        <ListItem key={"dynFilterFacet-" + item.filterValue} sx={{ paddingTop: '0px !important' }}>
                                                            <Link scroll={false} href={getDynMenuLink(df.name, item.filterValue, false)}>
                                                                <Button
                                                                    onClick={() => handleCloseFilterPopover()}
                                                                    size="small"
                                                                    className={isSelected ? cssStyles.active_filter_list : cssStyles.filter_list}
                                                                    sx={{
                                                                        borderWidth: '1px',
                                                                        borderStyle: 'solid',
                                                                        borderColor: isSelected ? 'primary.main' : 'secondary.dark',
                                                                        color: 'secondary.dark',
                                                                        '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                                                                    }}
                                                                    endIcon={isSelected ? <CheckIcon sx={{ color: 'primary.main' }} /> : ""}
                                                                >
                                                                    {item.hexaCode && (item.filterValue === "white"
                                                                        ? <CircleOutlinedIcon fontSize="large" style={{ fill: '#000000' }} />
                                                                        : <CircleIcon fontSize="large" style={{ fill: item.hexaCode }} />)}
                                                                    <Typography className={cssStyles.menu_text} sx={{
                                                                        whiteSpace: 'nowrap',
                                                                        color: isSelected ? 'primary.main' : 'secondary.dark',
                                                                        '&:hover': { color: 'primary.main' },
                                                                    }}>
                                                                        {item?.displayname} : {item.count}
                                                                    </Typography>
                                                                </Button>
                                                            </Link>
                                                        </ListItem>
                                                    );
                                                })}
                                            </List>
                                        )}
                                    </React.Fragment>
                                ))}
                            </Box>
                        </Popover>
                    </Box>
                </>
            ) : (
                <>
                    <Box component="div" sx={{ display: 'flex', alignItems: 'center' }}>
                        <Button color="secondary" onClick={toggleFilterMobile(true)} sx={{
                            fontSize: '12px', padding: '0 10px', margin: '10px 0', minWidth: 'fit-content',
                        }}>Filter By</Button>
                        <Box component="div" sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>{getSaleFilter()}</Box>
                        <Box component="div" sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>{buildSortByMenu()}</Box>
                    </Box>
                    <SwipeableDrawer
                        anchor="bottom"
                        open={openFilterMobile}
                        onClose={toggleFilterMobile(false)}
                        onOpen={toggleFilterMobile(true)}
                        sx={{ zIndex: '1201' }}
                    >
                        <div className={cssStyles.filter_mobile_header}>
                            <Box component="div" className={cssStyles.filter_by_label}>
                                <Typography className={cssStyles.sub_heading}>FILTER BY</Typography>
                            </Box>
                            <Button color="secondary" className={cssStyles.filter_close_cta} onClick={toggleFilterMobile(false)}>Close</Button>
                        </div>
                        {dynamicFilters.map((df: DynamicFilter) => {
                            if (df.name !== 'sale') {
                                return (
                                    <Accordion key={"dynFilter-" + df.name} slotProps={{ transition: { unmountOnExit: true } }} className={cssStyles.mobile_filter_drawer}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />} className={cssStyles.mobile_filter_label}>
                                            {df.name}
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            {df.filterItems?.map((item: DynamicFilterItem) => {
                                                const isSelected = df?.selectedFilterValues?.includes(item.filterValue) || false;
                                                return (
                                                    <Link key={"dynFilterFacet-" + item.filterValue} scroll={false} href={getDynMenuLink(df.name, item.filterValue, false)}>
                                                        <Button
                                                            size="small"
                                                            className={isSelected ? cssStyles.active_filter_list : cssStyles.filter_list}
                                                            sx={{
                                                                borderWidth: '1px',
                                                                borderStyle: 'solid',
                                                                borderColor: isSelected ? 'primary.main' : 'secondary.dark',
                                                                color: 'secondary.dark',
                                                                '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                                                                marginBottom: '8px !important',
                                                            }}
                                                            endIcon={isSelected ? <CheckIcon sx={{ color: 'primary.main' }} /> : ""}
                                                        >
                                                            {item.hexaCode && (item.filterValue === "white"
                                                                ? <CircleOutlinedIcon fontSize="large" style={{ fill: '#000000' }} />
                                                                : <CircleIcon fontSize="large" style={{ fill: item.hexaCode }} />)}
                                                            <Typography className={cssStyles.menu_text} sx={{
                                                                whiteSpace: 'nowrap',
                                                                color: isSelected ? 'primary.main' : 'secondary.dark',
                                                                '&:hover': { color: 'primary.main' },
                                                            }}>
                                                                {item?.displayname} : {item.count}
                                                            </Typography>
                                                        </Button>
                                                    </Link>
                                                );
                                            })}
                                        </AccordionDetails>
                                    </Accordion>
                                );
                            }
                            return null;
                        })}
                    </SwipeableDrawer>
                </>
            )}

            {/* Selected filters + Clear All */}
            <Box component='div' sx={{
                borderTop: "1px solid #eeeeee",
                borderTopColor: 'secondary.main',
                display: 'flex', alignItems: 'center', padding: '5px 0', columnGap: '10px',
            }}>
                {dynamicFilters.map((df: DynamicFilter) => {
                    return !df.hideFromSelection && df.selectedFilterValues?.map((filterValue: string) => {
                        const displayName = getDisplayName(filterValue, df.name);
                        return (
                            <Link key={"selected-" + filterValue} scroll={false} href={getDynMenuLink(df.name, filterValue, true)}>
                                <Button
                                    className={cssStyles.filter_list}
                                    size="small"
                                    sx={{
                                        padding: "5px 10px", margin: "2px",
                                        borderWidth: '1px',
                                        borderStyle: 'solid',
                                        color: 'secondary.dark', borderColor: 'secondary.dark',
                                        '&:hover': { borderColor: 'primary.main', color: 'primary.main', boxShadow: 'none' },
                                    }}
                                    endIcon={<ClearIcon sx={{ color: 'secondary.dark' }} />}
                                >
                                    <Typography className={cssStyles.menu_text} sx={{ color: 'secondary.dark', '&:hover': { color: 'primary.main' } }}>
                                        {displayName || filterValue}
                                    </Typography>
                                </Button>
                            </Link>
                        );
                    });
                })}
                {dynamicFilters.find((df: DynamicFilter) =>
                    !df.hideFromSelection && df.selectedFilterValues && df.selectedFilterValues.length > 0) && (
                    <Link scroll={false} href={getDynMenuLink(null, null, false)}>
                        <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>CLEAR ALL</Typography>
                    </Link>
                )}
            </Box>
        </div>
    );
};

export default DynamicFilterPanel;
